import type { ClientGroup, HealthItem, WebsiteRow } from '@@/api/health/health-rollup';
import { useNavigate } from 'react-router';
import { ChevronRight } from 'lucide-react';
import { cn } from 'helpers/cn';
import { CountBadge, EmptyState, FOCUS_RING, Panel, SeverityDot } from '../ConsolePanel';
import { describeAttention } from '../Home/attention-descriptors';

type ClientCardProps = {
  client: ClientGroup;
  /** Set on the first card only, for the product tour. */
  tourAnchor?: string;
};

type SeverityBadgesProps = { broken: number; decide: number; queue: number };

/**
 * Counts, shown only when non-zero.
 *
 * A row of three zeroes on every healthy account is noise, and it makes the one
 * account that does need something harder to spot rather than easier.
 */
const SeverityBadges = ({ broken, decide, queue }: SeverityBadgesProps) => {
  if (broken + decide + queue === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {broken > 0 && <CountBadge severity="broken">{broken} broken</CountBadge>}
      {decide > 0 && <CountBadge severity="decide">{decide} to decide</CountBadge>}
      {queue > 0 && <CountBadge severity="queue">{queue} to build</CountBadge>}
    </div>
  );
};

type ItemRowProps = {
  item: HealthItem;
  /** True on the first row of the first card only, for the product tour. */
  isTourAnchor: boolean;
};

/** One experiment that needs something, with the sentence explaining why. */
const ItemRow = ({ item, isTourAnchor }: ItemRowProps) => {
  const navigate = useNavigate();
  // Reuses the dashboard attention card's copy, so the same signal reads
  // identically wherever it surfaces.
  const descriptor = describeAttention(item);

  return (
    <button
      type="button"
      onClick={() => navigate(descriptor.href(item))}
      data-tour={isTourAnchor ? 'health-item' : undefined}
      title={`${descriptor.title(item)} — ${descriptor.detail(item)}`}
      // Two stacked lines below `md`, one row from there. At 375px the fixed 35px row
      // left the detail about 65 pixels after the title's 45% and the chevron, so the
      // sentence explaining why the experiment needs a person was truncated away.
      className={cn(
        'hover:bg-hover flex min-h-9 w-full items-center gap-2.5 rounded-card px-2.5 py-1.5 text-left',
        'transition-colors duration-150 md:h-[35px] md:py-0',
        FOCUS_RING,
      )}
    >
      <SeverityDot severity={item.severity} />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5 md:flex-row md:items-center md:gap-2.5">
        <span className="min-w-0 truncate text-meta md:max-w-[45%] md:shrink-0">{descriptor.title(item)}</span>
        <span className="supporting min-w-0 truncate md:flex-1">{descriptor.detail(item)}</span>
      </span>
      <ChevronRight className="text-dim size-3.5 shrink-0" strokeWidth={1.5} />
    </button>
  );
};

type WebsiteBlockProps = {
  website: WebsiteRow;
  /** Puts the product tour's item anchor on this block's first row. */
  anchorFirstItem: boolean;
};

const WebsiteBlock = ({ website, anchorFirstItem }: WebsiteBlockProps) => {
  const navigate = useNavigate();

  return (
    <div className="border-line rounded-card border">
      <button
        type="button"
        onClick={() => navigate(`/?ws=${website.websiteId}`)}
        title={website.url}
        className={cn(
          'hover:bg-hover flex w-full items-center justify-between gap-2 rounded-card px-2.5 py-2 text-left',
          'transition-colors duration-150',
          FOCUS_RING,
        )}
      >
        <span className="min-w-0 truncate text-meta">{website.url}</span>
        <span className="micro-label shrink-0">{website.runningCount} running</span>
      </button>

      {website.items.length > 0 && (
        <div className="border-line border-t p-1">
          {website.items.map((item, idx) => (
            <ItemRow
              key={`${item.kind}:${item.experimentId}`}
              item={item}
              isTourAnchor={anchorFirstItem && idx === 0}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const ClientCard = ({ client, tourAnchor }: ClientCardProps) => {
  const { totals } = client;
  // Only websites with something to say. A client with forty sites and one
  // problem should show one row, not forty.
  const websites = client.websites.filter((website) => website.items.length > 0 || website.runningCount > 0);
  // The tour's item anchor goes on the first website in the first card that has something to fix.
  const tourItemWebsite = tourAnchor ? websites.find((website) => website.items.length > 0) : undefined;

  return (
    <Panel
      title={client.companyName}
      description={`${totals.runningCount} running · ${client.websites.length} website${client.websites.length === 1 ? '' : 's'}`}
      // A broken item is the one state worth spending the panel's own outline on.
      className={cn(totals.brokenCount > 0 && 'border-danger-line')}
      actions={<SeverityBadges broken={totals.brokenCount} decide={totals.decideCount} queue={totals.queueCount} />}
      bodyClassName="flex flex-col gap-2 p-2"
      tourAnchor={tourAnchor}
    >
      {websites.length > 0 ? (
        websites.map((website) => (
          <WebsiteBlock key={website.websiteId} website={website} anchorFirstItem={website === tourItemWebsite} />
        ))
      ) : (
        <div className="px-1.5 py-1">
          <EmptyState>
            {client.websites.length === 0 ? 'No websites yet.' : 'Nothing running and nothing wrong.'}
          </EmptyState>
        </div>
      )}
    </Panel>
  );
};
